// src/components/SMSPhones/SMSPhones.js
import React, { useState, useEffect } from "react";
import { TextCancelButton, ErrorContainedButton } from "../button";
import {
  Backdrop,
  Box,
  CircularProgress,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { acme, acmeStatusChipSx, acmePageSectionCardSx, acmeBillingSectionContentSx } from "../../theme/acmeTheme";
import {
  PageShell,
  PageHeader,
  PageBlockTitle,
  Card,
  CardContent,
  Table,
  Dialog,
  DialogTitleText,
  DialogBody,
  DialogFooter,
  Button,
  Input,
  Chip,
  TableEmptyRow,
  MenuItem,
  TableActionsMenu,
  TableActionsMenuButton,
  destructiveMenuItemSx,
  stopTableRowClick,
} from "../ui";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import PageSnackbars from "../common/PageSnackbars";
import { usePageSnackbar } from "../common/usePageSnackbar";
import axios from "axios";

// Mirror of backend toAlphaSenderId: fold diacritics, keep letters/digits/
// spaces, max 11 chars, must contain a letter.
const toSenderPreview = (label) => {
  const folded = String(label || "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "");
  const cleaned = folded
    .replace(/[^A-Za-z0-9 ]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 11)
    .trim();
  return /[A-Za-z]/.test(cleaned) ? cleaned : "";
};

const PHONE_STATUS_LABELS = {
  draft: "Draft",
  otp_pending: "Pending verification",
  ownership_verified: "Ownership verified",
  provider_activation_pending: "Awaiting activation",
  kyc_required: "KYC required",
  loa_required: "LOA required",
  provisioning: "Provisioning",
  active: "Active",
  rejected: "Rejected",
  suspended: "Suspended",
};

const PHONE_STATUS_VARIANTS = {
  active: "success",
  otp_pending: "warning",
  ownership_verified: "warning",
  provider_activation_pending: "warning",
  kyc_required: "warning",
  loa_required: "warning",
  provisioning: "warning",
  rejected: "destructive",
  suspended: "destructive",
  draft: "neutral",
};

const getPhoneStatusKey = (p) => {
  if (!p.isVerified) return "otp_pending";
  if (p.status === "active" && p.providerActivationConfirmedAt) return "active";
  return p.status || "ownership_verified";
};

const getPhoneStatusLabel = (p) => {
  const key = getPhoneStatusKey(p);
  return PHONE_STATUS_LABELS[key] || key.replace(/_/g, " ");
};

const getPhoneStatusVariant = (p) =>
  PHONE_STATUS_VARIANTS[getPhoneStatusKey(p)] || "neutral";

const SMSPhones = () => {
  const [phones, setPhones] = useState([]);
  const [phone, setPhone] = useState("");
  const [displayLabel, setDisplayLabel] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [openVerifyDialog, setOpenVerifyDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedPhone, setSelectedPhone] = useState(null);
  const [phoneActionMenu, setPhoneActionMenu] = useState({ anchorEl: null, phone: null });
  const [loading, setLoading] = useState(false);
  const { setSuccessMessage, setErrorMessage, snackbarProps } =
    usePageSnackbar();
  const senderPreview = toSenderPreview(displayLabel);

  const fetchPhones = async () => {
    setLoading(true);
    try {
      const response = await axios.get("/api/sms-phones");
      setPhones(response.data);
    } catch (error) {
      console.error("Failed to fetch SMS phones:", error);
      setErrorMessage(
        error.response?.data?.message || "Failed to fetch SMS phones.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPhones();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSendCode = async () => {
    if (!phone) {
      setErrorMessage("Please enter a phone number.");
      return;
    }

    try {
      await axios.post("/api/sms-phones/send-verification-code", {
        phone,
        label: displayLabel || undefined,
      });
      setSuccessMessage(
        "Mailsball ownership verification code sent. Check your handset.",
      );
      setOpenVerifyDialog(true);
    } catch (error) {
      console.error("Failed to send verification code:", error);
      setErrorMessage(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Failed to send verification code.",
      );
    }
  };

  const handleVerify = async () => {
    try {
      await axios.post("/api/sms-phones/verify", {
        phone,
        code: verificationCode,
      });
      setSuccessMessage("Phone verified successfully.");
      setOpenVerifyDialog(false);
      setVerificationCode("");
      fetchPhones();
    } catch (error) {
      console.error("Failed to verify phone:", error);
      setErrorMessage(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Failed to verify phone.",
      );
    }
  };

  const handleDelete = async (phoneId) => {
    try {
      await axios.delete(`/api/sms-phones/${phoneId}`);
      setSuccessMessage("Phone deleted.");
      setOpenDeleteDialog(false);
      fetchPhones();
    } catch (error) {
      console.error("Failed to delete phone:", error);
      setErrorMessage(
        error.response?.data?.message || "Failed to delete phone.",
      );
    }
  };

  const openPhoneActionMenu = (event, phoneRow) => {
    event.stopPropagation();
    setPhoneActionMenu({ anchorEl: event.currentTarget, phone: phoneRow });
  };

  const closePhoneActionMenu = () => {
    setPhoneActionMenu({ anchorEl: null, phone: null });
  };

  const handlePhoneActionMenuItem = (action) => {
    const phoneRow = phoneActionMenu.phone;
    if (!phoneRow) return;
    closePhoneActionMenu();

    if (action === "enterCode") {
      setPhone(phoneRow.phone);
      setOpenVerifyDialog(true);
      return;
    }
    if (action === "delete") {
      setSelectedPhone(phoneRow);
      setOpenDeleteDialog(true);
    }
  };

  return (
    <PageShell>
      <PageHeader
        title="Verified Phones"
        description="Add your number, confirm ownership with the 6-digit code we send by SMS, and use it as a sender in SMS campaigns. Recipients will see the company name below as the sender."
        sx={{ mb: 2 }}
      />

      <PageSnackbars {...snackbarProps} />

      <Card sx={{ ...acmePageSectionCardSx, boxShadow: "none", mt: 0 }}>
        <CardContent sx={acmeBillingSectionContentSx}>
          <PageBlockTitle gutterBottom>Verified Phones</PageBlockTitle>

      <Box sx={{ mb: 2 }}>
          <Input
            label="Phone number (E.164, e.g. +491701234567)"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Input
            label="Company or name (sender shown to recipients)"
            value={displayLabel}
            onChange={(e) => setDisplayLabel(e.target.value)}
            inputProps={{ maxLength: 40 }}
            helperText={
              senderPreview
                ? `Recipients will see sender: "${senderPreview}"`
                : "Letters/digits, max 11 characters (e.g. ACME). Needs at least one letter."
            }
            sx={{ mb: 2 }}
          />
          <Button variant="default" onClick={handleSendCode} fullWidth>
            Send verification code
          </Button>
      </Box>

      <Table containerSx={{ mt: 2 }}>
        <TableHead>
          <TableRow>
            <TableCell>Phone</TableCell>
            <TableCell>Label</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {phones.length > 0 ? (
            phones.map((p) => (
              <TableRow key={p._id}>
                <TableCell>{p.e164 || p.phone}</TableCell>
                <TableCell>{p.label || "—"}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={getPhoneStatusLabel(p)}
                    sx={acmeStatusChipSx(getPhoneStatusVariant(p))}
                  />
                </TableCell>
                <TableCell align="right" onClick={stopTableRowClick}>
                  <TableActionsMenuButton
                    ariaLabel="Phone actions"
                    onClick={(event) => openPhoneActionMenu(event, p)}
                  />
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableEmptyRow
              colSpan={4}
              icon={PhoneAndroidIcon}
              title="No verified phones yet"
              description="Add and verify a phone number to use it as a sender in SMS campaigns. Recipients will see your company name as the sender."
              hint="Enter a number above and send the verification code to get started."
            />
          )}
        </TableBody>
      </Table>

        </CardContent>
      </Card>

      <Dialog
        open={openVerifyDialog}
        onClose={() => setOpenVerifyDialog(false)}
      >
        <DialogTitleText>Verify phone</DialogTitleText>
        <DialogBody>
          <Typography
            variant="body2"
            sx={{ color: acme.colors.mutedForegroundSolid, mb: 2 }}
          >
            Enter the 6-digit Mailsball code sent to {phone}.
          </Typography>
          <Input
            autoFocus
            label="Verification code"
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value)}
          />
        </DialogBody>
        <DialogFooter>
          <TextCancelButton onClick={() => setOpenVerifyDialog(false)}>
            Cancel
          </TextCancelButton>
          <Button variant="default" onClick={handleVerify}>
            Verify
          </Button>
        </DialogFooter>
      </Dialog>

      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
      >
        <DialogTitleText>Delete phone</DialogTitleText>
        <DialogBody>
          <Typography
            variant="body2"
            sx={{ color: acme.colors.mutedForegroundSolid }}
          >
            Remove {selectedPhone?.e164 || selectedPhone?.phone} from your
            account?
          </Typography>
        </DialogBody>
        <DialogFooter>
          <TextCancelButton onClick={() => setOpenDeleteDialog(false)}>
            Cancel
          </TextCancelButton>
          <ErrorContainedButton onClick={() => handleDelete(selectedPhone._id)}>
            Delete
          </ErrorContainedButton>
        </DialogFooter>
      </Dialog>

      <TableActionsMenu
        anchorEl={phoneActionMenu.anchorEl}
        open={Boolean(phoneActionMenu.anchorEl)}
        onClose={closePhoneActionMenu}
      >
        {!phoneActionMenu.phone?.isVerified &&
          phoneActionMenu.phone?.status === "otp_pending" && (
            <MenuItem onClick={() => handlePhoneActionMenuItem("enterCode")}>
              Enter code
            </MenuItem>
          )}
        <MenuItem
          onClick={() => handlePhoneActionMenuItem("delete")}
          sx={destructiveMenuItemSx}
        >
          Delete
        </MenuItem>
      </TableActionsMenu>

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </PageShell>
  );
};

export default SMSPhones;
